import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom"
import Document from "@tiptap/extension-document"
import HardBreak from "@tiptap/extension-hard-break"
import History from "@tiptap/extension-history"
import Mention from "@tiptap/extension-mention"
import Paragraph from "@tiptap/extension-paragraph"
import Placeholder from "@tiptap/extension-placeholder"
import Text from "@tiptap/extension-text"
import { EditorContent, Extension, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from "@tiptap/react"
import type { Editor, NodeViewProps } from "@tiptap/react"
import Suggestion from "@tiptap/suggestion"
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion"
import { ChevronRight, FileText, Folder, Terminal, Zap } from "lucide-react"
import { observer } from "mobx-react"
import type React from "react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { twMerge } from "tailwind-merge"
import { usePortalContainer } from "../hooks/usePortalContainer"
import type { TreeMatch } from "../nestAPIs/files"
import { fuzzySearch, isFilesApiAvailable } from "../nestAPIs/files"
import type { FsWorkspaceContext } from "../nestAPIs/files"
import type { SdkCapabilitiesManager, SlashCommandEntry } from "../store/managers/SdkCapabilitiesManager"
import type { SmartEditorManager } from "../store/managers/SmartEditorManager"
import { processImageBlob } from "../utils/imageAttachment"
import { getFileName } from "./utils/paths"

interface SmartEditorProps {
    /** Required: manager instance for state management */
    manager: SmartEditorManager
    placeholder?: string
    disabled?: boolean
    className?: string
    editorClassName?: string
    onKeyDown?: (e: React.KeyboardEvent) => void
    onUserInput?: () => void
    /** Directory for @file mention autocomplete, null to disable */
    fileMentionsDir: string | null
    fileMentionsContext?: FsWorkspaceContext | null
    /** Directory for /slash command autocomplete, null to disable */
    slashCommandsDir: string | null
    /** SDK capabilities manager for slash command discovery */
    sdkCapabilities?: SdkCapabilitiesManager
}

export interface SmartEditorRef {
    focus: () => void
    focusEnd: () => void
    blur: () => void
    clear: () => void
}

function FileMentionChip({ node }: NodeViewProps) {
    return (
        <NodeViewWrapper as="span" className="bg-primary/10 text-primary text-sm px-1.5 py-0.5 inline-flex items-center gap-1">
            <FileText size="0.875em" />
            {node.attrs.id}
        </NodeViewWrapper>
    )
}

interface SuggestionItem {
    type: "file" | "dir"
    name: string
    fullPath: string
}

interface SuggestionListProps {
    items: SuggestionItem[]
    treeMatch: TreeMatch | null
    selectedIndex: number
    onSelectIndex: (index: number) => void
    onSelectItem: (index: number) => void
}

function SuggestionList({ items, treeMatch, selectedIndex, onSelectIndex, onSelectItem }: SuggestionListProps) {
    const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

    const displayItems: SuggestionItem[] = treeMatch
        ? treeMatch.children.map((child) => ({
              type: child.isDir ? ("dir" as const) : ("file" as const),
              name: child.name,
              fullPath: child.fullPath,
          }))
        : items

    useEffect(() => {
        const el = itemRefs.current.get(selectedIndex)
        el?.scrollIntoView({ block: "nearest" })
    }, [selectedIndex])

    if (displayItems.length === 0) {
        return (
            <div className="min-w-64">
                <div className="text-sm text-muted">No files found</div>
            </div>
        )
    }

    return (
        <div className="min-w-64 max-h-64 overflow-y-auto -m-3">
            {treeMatch?.path && (
                <div className="px-3 py-1.5 text-xs border-b border-border flex items-center gap-1 text-muted bg-base-300">
                    <Folder size="1em" />
                    <span>{treeMatch.path}/</span>
                </div>
            )}
            {displayItems.map((item, index) => (
                <button
                    key={item.fullPath}
                    ref={(el) => {
                        if (el) itemRefs.current.set(index, el)
                        else itemRefs.current.delete(index)
                    }}
                    type="button"
                    className={twMerge(
                        "btn w-full flex items-center gap-2 px-3 py-2 text-sm text-left cursor-pointer",
                        index === selectedIndex ? "bg-primary text-primary-content" : "text-base-content hover:bg-base-300"
                    )}
                    onClick={() => onSelectItem(index)}
                    onMouseEnter={() => onSelectIndex(index)}
                >
                    {item.type === "dir" ? (
                        <Folder size="1em" className={twMerge("flex-shrink-0", index === selectedIndex ? "text-primary-content/70" : "text-muted")} />
                    ) : (
                        <FileText size="1em" className={twMerge("flex-shrink-0", index === selectedIndex ? "text-primary-content/70" : "text-muted")} />
                    )}
                    <span className="truncate flex-1">{treeMatch ? item.name : item.fullPath}</span>
                    {item.type === "dir" && (
                        <ChevronRight size="1em" className={twMerge("flex-shrink-0", index === selectedIndex ? "text-primary-content/70" : "text-muted")} />
                    )}
                </button>
            ))}
        </div>
    )
}

// ============================================================================
// Slash Command Suggestion List
// ============================================================================

interface SlashSuggestionListProps {
    items: SlashCommandEntry[]
    selectedIndex: number
    onSelectIndex: (index: number) => void
    onSelectItem: (index: number) => void
}

function SlashSuggestionList({ items, selectedIndex, onSelectIndex, onSelectItem }: SlashSuggestionListProps) {
    const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

    useEffect(() => {
        const el = itemRefs.current.get(selectedIndex)
        el?.scrollIntoView({ block: "nearest" })
    }, [selectedIndex])

    if (items.length === 0) {
        return (
            <div className="min-w-64">
                <div className="text-sm text-muted">No matching commands</div>
            </div>
        )
    }

    return (
        <div className="min-w-72 max-h-64 overflow-y-auto -m-3">
            {items.map((cmd, index) => {
                const Icon = cmd.type === "skill" ? Zap : Terminal
                return (
                    <button
                        key={cmd.name}
                        ref={(el) => {
                            if (el) itemRefs.current.set(index, el)
                            else itemRefs.current.delete(index)
                        }}
                        type="button"
                        className={twMerge(
                            "btn w-full flex items-center gap-2 px-3 py-2 text-sm text-left cursor-pointer",
                            index === selectedIndex ? "bg-primary text-primary-content" : "text-base-content hover:bg-base-300"
                        )}
                        onClick={() => onSelectItem(index)}
                        onMouseEnter={() => onSelectIndex(index)}
                    >
                        <Icon size="1em" className={twMerge("flex-shrink-0", index === selectedIndex ? "text-primary-content/70" : "text-muted")} />
                        <span className="truncate flex-1">/{cmd.name}</span>
                    </button>
                )
            })}
        </div>
    )
}

// ============================================================================
// Helpers
// ============================================================================

function readEditorDoc(editor: Editor): { value: string; files: string[] } {
    let text = ""
    const files: string[] = []
    editor.state.doc.descendants((node) => {
        if (node.type.name === "text") {
            text += node.text
        } else if (node.type.name === "mention") {
            text += `@${node.attrs.id}`
            files.push(node.attrs.id)
        } else if (node.type.name === "hardBreak") {
            text += "\n"
        } else if (node.type.name === "paragraph") {
            if (text.length > 0 && !text.endsWith("\n")) {
                text += "\n"
            }
        }
    })
    return { value: text.trim(), files }
}

// ============================================================================
// Suggestion popup state types
// ============================================================================

interface FileSuggestionPopupState {
    open: boolean
    items: SuggestionItem[]
    treeMatch: TreeMatch | null
    selectedIndex: number
    anchorRect: DOMRect | null
}

interface SlashSuggestionPopupState {
    open: boolean
    items: SlashCommandEntry[]
    selectedIndex: number
    anchorRect: DOMRect | null
}

// ============================================================================
// SmartEditor
// ============================================================================

export const SmartEditor = observer(
    forwardRef<SmartEditorRef, SmartEditorProps>(
        (
            {
                manager,
                placeholder,
                disabled,
                className,
                editorClassName,
                onKeyDown,
                onUserInput,
                fileMentionsDir,
                fileMentionsContext,
                slashCommandsDir,
                sdkCapabilities,
            },
            ref
        ) => {
            const portalContainer = usePortalContainer()
            const onKeyDownRef = useRef(onKeyDown)
            onKeyDownRef.current = onKeyDown
            const onUserInputRef = useRef(onUserInput)
            onUserInputRef.current = onUserInput

            // --- File mention suggestion state (@ trigger) ---
            const [fileSuggestion, setFileSuggestion] = useState<FileSuggestionPopupState>({
                open: false,
                items: [],
                treeMatch: null,
                selectedIndex: 0,
                anchorRect: null,
            })

            // --- Slash command suggestion state (/ trigger) ---
            const [slashSuggestion, setSlashSuggestion] = useState<SlashSuggestionPopupState>({
                open: false,
                items: [],
                selectedIndex: 0,
                anchorRect: null,
            })

            // Determine if features should be enabled
            const mentionsEnabled = fileMentionsDir !== null && isFilesApiAvailable()
            const slashEnabled = slashCommandsDir !== null
            const fileMentionsContextWorkspaceId = fileMentionsContext?.workspaceId
            const fileMentionsContextWorkspaceRoot = fileMentionsContext?.workspaceRoot
            const fileMentionsContextWorktreeId = fileMentionsContext?.worktreeId
            const stableFileMentionsContext = useMemo<FsWorkspaceContext | undefined>(() => {
                if (fileMentionsContextWorkspaceId === undefined || fileMentionsContextWorkspaceRoot === undefined) return undefined
                return {
                    workspaceId: fileMentionsContextWorkspaceId,
                    workspaceRoot: fileMentionsContextWorkspaceRoot,
                    ...(fileMentionsContextWorktreeId !== undefined ? { worktreeId: fileMentionsContextWorktreeId } : {}),
                }
            }, [fileMentionsContextWorkspaceId, fileMentionsContextWorkspaceRoot, fileMentionsContextWorktreeId])
            const fileMentionsScopeKey = `${fileMentionsDir ?? ""}\0${fileMentionsContextWorkspaceId ?? ""}\0${fileMentionsContextWorkspaceRoot ?? ""}\0${fileMentionsContextWorktreeId ?? ""}`
            const fileMentionsScopeRef = useRef(fileMentionsScopeKey)
            fileMentionsScopeRef.current = fileMentionsScopeKey
            const fileSuggestionPropsRef = useRef<SuggestionProps<string> | null>(null)
            const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
            const fileSearchRequestRef = useRef(0)
            const lastQueryRef = useRef<string>("")

            // Load SDK capabilities when slashCommandsDir is provided
            useEffect(() => {
                if (slashCommandsDir && sdkCapabilities) {
                    void sdkCapabilities.loadCapabilities(slashCommandsDir)
                }
            }, [slashCommandsDir, sdkCapabilities])

            // A task can switch between its original root and an agent checkout
            // while a completion request is in flight. Close the old popup and
            // invalidate that request before accepting results for the new scope.
            useEffect(() => {
                fileSearchRequestRef.current += 1
                lastQueryRef.current = ""
                if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current)
                    searchTimeoutRef.current = null
                }
                fileSuggestionPropsRef.current = null
                setFileSuggestion({
                    open: false,
                    items: [],
                    treeMatch: null,
                    selectedIndex: 0,
                    anchorRect: null,
                })
            }, [fileMentionsScopeKey])

            // Warm up file search cache when fileMentionsDir is provided
            useEffect(() => {
                if (fileMentionsDir && isFilesApiAvailable()) {
                    fuzzySearch({ dir: fileMentionsDir, query: "", matchDirs: false, limit: 20, context: stableFileMentionsContext }).catch(() => {})
                }
            }, [fileMentionsDir, stableFileMentionsContext])

            const slashCommands = slashEnabled && sdkCapabilities ? sdkCapabilities.allCommands : []

            // --- File mention floating ---
            const { refs: fileRefs, floatingStyles: fileFloatingStyles } = useFloating({
                open: fileSuggestion.open,
                placement: "bottom-start",
                middleware: [offset(8), flip(), shift({ padding: 8 })],
                whileElementsMounted: autoUpdate,
            })

            useEffect(() => {
                if (fileSuggestion.anchorRect) {
                    fileRefs.setReference({
                        getBoundingClientRect: () => fileSuggestion.anchorRect as DOMRect,
                    })
                }
            }, [fileSuggestion.anchorRect, fileRefs])

            // --- Slash command floating ---
            const { refs: slashRefs, floatingStyles: slashFloatingStyles } = useFloating({
                open: slashSuggestion.open,
                placement: "top-start",
                middleware: [offset(8), flip(), shift({ padding: 8 })],
                whileElementsMounted: autoUpdate,
            })

            useEffect(() => {
                if (slashSuggestion.anchorRect) {
                    slashRefs.setReference({
                        getBoundingClientRect: () => slashSuggestion.anchorRect as DOMRect,
                    })
                }
            }, [slashSuggestion.anchorRect, slashRefs])

            // --- File mention logic ---
            const handleFileSearch = useCallback(
                async (query: string) => {
                    if (!fileMentionsDir) return

                    if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current)
                    }

                    lastQueryRef.current = query
                    const request = ++fileSearchRequestRef.current
                    const scopeKey = fileMentionsScopeKey

                    const delay = query ? 150 : 50
                    searchTimeoutRef.current = setTimeout(async () => {
                        if (scopeKey !== fileMentionsScopeRef.current || request !== fileSearchRequestRef.current) return
                        try {
                            const result = await fuzzySearch({
                                dir: fileMentionsDir,
                                query,
                                matchDirs: false,
                                limit: 20,
                                context: stableFileMentionsContext,
                            })
                            if (scopeKey === fileMentionsScopeRef.current && request === fileSearchRequestRef.current && query === lastQueryRef.current) {
                                const items = result.results.map((path) => ({
                                    type: "file" as const,
                                    name: getFileName(path),
                                    fullPath: path,
                                }))
                                setFileSuggestion((prev) => ({
                                    ...prev,
                                    items,
                                    treeMatch: result.treeMatch || null,
                                    selectedIndex: 0,
                                }))
                            }
                        } catch (err) {
                            console.error("[SmartEditor] Search failed:", err)
                            if (scopeKey === fileMentionsScopeRef.current && request === fileSearchRequestRef.current && query === lastQueryRef.current) {
                                setFileSuggestion((prev) => ({
                                    ...prev,
                                    items: [],
                                    treeMatch: null,
                                }))
                            }
                        }
                    }, delay)
                },
                [fileMentionsDir, fileMentionsScopeKey, stableFileMentionsContext]
            )

            const fileDisplayItems: SuggestionItem[] = fileSuggestion.treeMatch
                ? fileSuggestion.treeMatch.children.map((child) => ({
                      type: child.isDir ? ("dir" as const) : ("file" as const),
                      name: child.name,
                      fullPath: child.fullPath,
                  }))
                : fileSuggestion.items

            const handleFileSelectItem = useCallback(
                (index: number) => {
                    const item = fileDisplayItems[index]
                    if (!item) return

                    const props = fileSuggestionPropsRef.current
                    if (!props) return

                    if (item.type === "dir") {
                        const { editor: e, range } = props
                        e.chain().focus().deleteRange(range).insertContent(`@${item.fullPath}`).run()
                    } else {
                        const { editor: e, range } = props
                        e.chain()
                            .focus()
                            .deleteRange(range)
                            .insertContent([
                                { type: "mention", attrs: { id: item.fullPath } },
                                { type: "text", text: " " },
                            ])
                            .run()
                    }
                },
                [fileDisplayItems]
            )

            const handleFileKeyDown = useCallback(
                (event: KeyboardEvent): boolean => {
                    if (event.key === "Escape") {
                        return true
                    }
                    if (event.key === "ArrowUp") {
                        setFileSuggestion((prev) => ({
                            ...prev,
                            selectedIndex: (prev.selectedIndex + fileDisplayItems.length - 1) % Math.max(fileDisplayItems.length, 1),
                        }))
                        return true
                    }
                    if (event.key === "ArrowDown") {
                        setFileSuggestion((prev) => ({
                            ...prev,
                            selectedIndex: (prev.selectedIndex + 1) % Math.max(fileDisplayItems.length, 1),
                        }))
                        return true
                    }
                    if (event.key === "Enter" && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                        handleFileSelectItem(fileSuggestion.selectedIndex)
                        return true
                    }
                    return false
                },
                [fileDisplayItems, handleFileSelectItem, fileSuggestion.selectedIndex]
            )

            const fileKeyDownRef = useRef(handleFileKeyDown)
            useEffect(() => {
                fileKeyDownRef.current = handleFileKeyDown
            }, [handleFileKeyDown])

            // --- Slash command logic ---
            const slashSuggestionPropsRef = useRef<SuggestionProps<SlashCommandEntry> | null>(null)

            // Keep a ref to the latest slashCommands so the suggestion plugin can access it
            const slashCommandsRef = useRef(slashCommands)
            useEffect(() => {
                slashCommandsRef.current = slashCommands
            }, [slashCommands])

            const handleSlashSelectItem = useCallback(
                (index: number) => {
                    const item = slashSuggestion.items[index]
                    if (!item) return

                    const props = slashSuggestionPropsRef.current
                    if (!props) return

                    // Replace the /query with /<command> (as plain text, not a mention node)
                    const { editor: e, range } = props
                    e.chain().focus().deleteRange(range).insertContent(`/${item.name} `).run()
                },
                [slashSuggestion.items]
            )

            const handleSlashKeyDown = useCallback(
                (event: KeyboardEvent): boolean => {
                    if (event.key === "Escape") {
                        return true
                    }
                    if (event.key === "ArrowUp") {
                        setSlashSuggestion((prev) => ({
                            ...prev,
                            selectedIndex: (prev.selectedIndex + prev.items.length - 1) % Math.max(prev.items.length, 1),
                        }))
                        return true
                    }
                    if (event.key === "ArrowDown") {
                        setSlashSuggestion((prev) => ({
                            ...prev,
                            selectedIndex: (prev.selectedIndex + 1) % Math.max(prev.items.length, 1),
                        }))
                        return true
                    }
                    if ((event.key === "Enter" || event.key === "Tab") && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                        handleSlashSelectItem(slashSuggestion.selectedIndex)
                        return true
                    }
                    return false
                },
                [handleSlashSelectItem, slashSuggestion.selectedIndex]
            )

            const slashKeyDownRef = useRef(handleSlashKeyDown)
            useEffect(() => {
                slashKeyDownRef.current = handleSlashKeyDown
            }, [handleSlashKeyDown])

            // --- Build extensions ---
            const extensions = useMemo(() => {
                const baseExtensions = [
                    Document,
                    Paragraph,
                    Text,
                    HardBreak,
                    History,
                    Placeholder.configure({
                        placeholder: placeholder ?? "What would you like to do?",
                    }),
                ]

                if (mentionsEnabled) {
                    baseExtensions.push(
                        Mention.extend({
                            addNodeView() {
                                return ReactNodeViewRenderer(FileMentionChip)
                            },
                        }).configure({
                            HTMLAttributes: {
                                class: "file-mention",
                            },
                            suggestion: {
                                char: "@",
                                items: ({ query }: { query: string }) => {
                                    handleFileSearch(query)
                                    return []
                                },
                                render: () => ({
                                    onStart: (props: SuggestionProps<string>) => {
                                        fileSuggestionPropsRef.current = props
                                        const rect = props.clientRect?.()
                                        setFileSuggestion((prev) => ({
                                            ...prev,
                                            open: true,
                                            selectedIndex: 0,
                                            anchorRect: rect ?? null,
                                        }))
                                    },
                                    onUpdate: (props: SuggestionProps<string>) => {
                                        fileSuggestionPropsRef.current = props
                                        const rect = props.clientRect?.()
                                        if (rect) {
                                            setFileSuggestion((prev) => ({
                                                ...prev,
                                                anchorRect: rect,
                                            }))
                                        }
                                    },
                                    onKeyDown: (props: SuggestionKeyDownProps) => {
                                        return fileKeyDownRef.current(props.event)
                                    },
                                    onExit: () => {
                                        fileSuggestionPropsRef.current = null
                                        setFileSuggestion({
                                            open: false,
                                            items: [],
                                            treeMatch: null,
                                            selectedIndex: 0,
                                            anchorRect: null,
                                        })
                                    },
                                }),
                            },
                        })
                    )
                }

                // Slash command suggestion (/ trigger)
                if (slashEnabled) {
                    baseExtensions.push(
                        Extension.create({
                            name: "slashCommand",
                            addProseMirrorPlugins() {
                                return [
                                    Suggestion({
                                        editor: this.editor,
                                        char: "/",
                                        startOfLine: true,
                                        items: ({ query }) => {
                                            const commands = slashCommandsRef.current ?? []
                                            const lower = query.toLowerCase()
                                            return commands.filter((cmd) => cmd.name.toLowerCase().includes(lower))
                                        },
                                        command: ({ editor: e, range, props: item }) => {
                                            const cmd = item as unknown as SlashCommandEntry
                                            e.chain().focus().deleteRange(range).insertContent(`/${cmd.name} `).run()
                                        },
                                        render: () => ({
                                            onStart: (props: SuggestionProps<SlashCommandEntry>) => {
                                                slashSuggestionPropsRef.current = props
                                                const rect = props.clientRect?.()
                                                setSlashSuggestion({
                                                    open: true,
                                                    items: props.items,
                                                    selectedIndex: 0,
                                                    anchorRect: rect ?? null,
                                                })
                                            },
                                            onUpdate: (props: SuggestionProps<SlashCommandEntry>) => {
                                                slashSuggestionPropsRef.current = props
                                                const rect = props.clientRect?.()
                                                setSlashSuggestion((prev) => ({
                                                    ...prev,
                                                    items: props.items,
                                                    selectedIndex: 0,
                                                    anchorRect: rect ?? prev.anchorRect,
                                                }))
                                            },
                                            onKeyDown: (props: SuggestionKeyDownProps) => {
                                                return slashKeyDownRef.current(props.event)
                                            },
                                            onExit: () => {
                                                slashSuggestionPropsRef.current = null
                                                setSlashSuggestion({
                                                    open: false,
                                                    items: [],
                                                    selectedIndex: 0,
                                                    anchorRect: null,
                                                })
                                            },
                                        }),
                                    }),
                                ]
                            },
                        })
                    )
                }

                return baseExtensions
            }, [mentionsEnabled, slashEnabled, placeholder, handleFileSearch])

            const editor = useEditor(
                {
                    extensions,
                    // Use stored JSON content if available, otherwise empty
                    content: manager.editorContent ?? undefined,
                    editable: !disabled,
                    onUpdate: ({ editor: e }) => {
                        const { value, files } = readEditorDoc(e)
                        manager.syncFromEditor({ value, files, editorContent: e.getJSON() })
                    },
                    editorProps: {
                        attributes: {
                            class: "outline-none h-full",
                        },
                        handleDOMEvents: {
                            beforeinput: () => {
                                onUserInputRef.current?.()
                                return false
                            },
                        },
                        handleDrop: (_view, event) => {
                            const files = event.dataTransfer?.files
                            if (files) {
                                for (const file of Array.from(files)) {
                                    if (file.type.startsWith("image/")) {
                                        event.preventDefault()
                                        processImageBlob(file)
                                            .then(({ attachment, dataUrl }) => manager.addImage(attachment, dataUrl))
                                            .catch((err) => console.error("[SmartEditor] Failed to process dropped image:", err))
                                        return true
                                    }
                                }
                            }
                            return false
                        },
                        handlePaste: (_view, event) => {
                            const items = event.clipboardData?.items
                            if (items) {
                                for (const item of Array.from(items)) {
                                    if (item.type.startsWith("image/")) {
                                        const file = item.getAsFile()
                                        if (file) {
                                            event.preventDefault()
                                            processImageBlob(file)
                                                .then(({ attachment, dataUrl }) => manager.addImage(attachment, dataUrl))
                                                .catch((err) => console.error("[SmartEditor] Failed to process pasted image:", err))
                                            return true
                                        }
                                    }
                                }
                            }
                            // Let TipTap handle text paste natively
                            return false
                        },
                        handleKeyDown: (_view, event) => {
                            const handleKeyDown = onKeyDownRef.current
                            if (handleKeyDown) {
                                const syntheticEvent = {
                                    key: event.key,
                                    metaKey: event.metaKey,
                                    ctrlKey: event.ctrlKey,
                                    shiftKey: event.shiftKey,
                                    altKey: event.altKey,
                                    preventDefault: () => event.preventDefault(),
                                    stopPropagation: () => event.stopPropagation(),
                                } as React.KeyboardEvent
                                handleKeyDown(syntheticEvent)
                                if (event.defaultPrevented) return true
                            }
                            return false
                        },
                    },
                },
                [extensions, manager]
            )

            useEffect(() => {
                if (editor) {
                    editor.setEditable(!disabled)
                }
            }, [editor, disabled])

            // Register clear callback so manager.clear() can clear the editor
            useEffect(() => {
                if (!editor) return

                const clearEditor = () => {
                    editor.commands.clearContent()
                }

                manager.registerClearCallback(clearEditor)
                return () => {
                    manager.unregisterClearCallback()
                }
            }, [editor, manager])

            // Register setContent callback so manager-driven restores can replace the full editor state.
            useEffect(() => {
                if (!editor) return

                const setContent = (content: string | Record<string, unknown> | null) => {
                    if (content === null) {
                        editor.commands.clearContent()
                    } else {
                        editor.commands.setContent(content)
                    }
                    editor.commands.focus("end")
                }

                manager.registerSetContentCallback(setContent)
                return () => {
                    manager.unregisterSetContentCallback()
                }
            }, [editor, manager])

            useImperativeHandle(
                ref,
                () => ({
                    focus: () => {
                        editor?.commands.focus()
                    },
                    focusEnd: () => {
                        editor?.commands.focus("end")
                    },
                    blur: () => {
                        editor?.commands.blur()
                    },
                    clear: () => {
                        // Just call manager.clear() - it will trigger the registered callback to clear the editor
                        manager.clear()
                    },
                }),
                [editor, manager]
            )

            const handleContainerClick = () => {
                if (!disabled) {
                    editor?.commands.focus()
                }
            }

            const [isDragOver, setIsDragOver] = useState(false)
            const dragCounterRef = useRef(0)

            const handleDragEnter = useCallback((e: React.DragEvent) => {
                e.preventDefault()
                dragCounterRef.current++
                if (e.dataTransfer?.types.includes("Files")) {
                    setIsDragOver(true)
                }
            }, [])

            const handleDragLeave = useCallback((e: React.DragEvent) => {
                e.preventDefault()
                dragCounterRef.current--
                if (dragCounterRef.current === 0) {
                    setIsDragOver(false)
                }
            }, [])

            const handleDragOver = useCallback((e: React.DragEvent) => {
                e.preventDefault()
            }, [])

            const handleDrop = useCallback((_e: React.DragEvent) => {
                dragCounterRef.current = 0
                setIsDragOver(false)
                // Actual drop handling is done by TipTap's handleDrop in editorProps
            }, [])

            return (
                <div className="relative w-full">
                    <div
                        className={twMerge(
                            "w-full bg-input text-base-content border border-border cursor-text",
                            "focus-within:border-primary transition-colors",
                            isDragOver && "border-primary ring-1 ring-primary/50",
                            disabled && "opacity-50 cursor-not-allowed",
                            className
                        )}
                        onClick={handleContainerClick}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <EditorContent
                            editor={editor}
                            className={twMerge(
                                "w-full h-full px-4 py-3 [&_.ProseMirror]:h-full [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted/50 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none",
                                editorClassName
                            )}
                            data-placeholder={placeholder}
                        />
                    </div>

                    {/* File mention popup (@ trigger) */}
                    {fileSuggestion.open &&
                        createPortal(
                            <div
                                ref={fileRefs.setFloating}
                                style={fileFloatingStyles}
                                className="z-50 bg-base-200 p-3 text-base-content shadow-lg outline outline-1 outline-border"
                            >
                                <SuggestionList
                                    items={fileSuggestion.items}
                                    treeMatch={fileSuggestion.treeMatch}
                                    selectedIndex={fileSuggestion.selectedIndex}
                                    onSelectIndex={(index) => setFileSuggestion((prev) => ({ ...prev, selectedIndex: index }))}
                                    onSelectItem={handleFileSelectItem}
                                />
                            </div>,
                            portalContainer ?? document.body
                        )}

                    {/* Slash command popup (/ trigger) */}
                    {slashSuggestion.open &&
                        slashSuggestion.items.length > 0 &&
                        createPortal(
                            <div
                                ref={slashRefs.setFloating}
                                style={slashFloatingStyles}
                                className="z-50 bg-base-200 p-3 text-base-content shadow-lg outline outline-1 outline-border"
                            >
                                <SlashSuggestionList
                                    items={slashSuggestion.items}
                                    selectedIndex={slashSuggestion.selectedIndex}
                                    onSelectIndex={(index) => setSlashSuggestion((prev) => ({ ...prev, selectedIndex: index }))}
                                    onSelectItem={handleSlashSelectItem}
                                />
                            </div>,
                            portalContainer ?? document.body
                        )}
                </div>
            )
        }
    )
)

SmartEditor.displayName = "SmartEditor"
